import React, { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Fab, Slide, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import { getHighlightParts } from 'services/Utilities/NewHighlights';
import Currency from 'react-currency-formatter';
import { useExteriorSignages } from 'services/Data/ExteriorSignageServices';
import useImage from 'services/Hooks/useImage';
import {
  useActiveExteriorSignageQuote,
  useDeleteExteriorSignageQuoteItem,
  usePostExteriorSignageQuoteItem,
} from 'services/Data/ExteriorSignageQuoteService';
import { useSelector } from 'react-redux';
import { Add, Remove } from '@mui/icons-material';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import DeleteModal from '@acnb/CustomComponents/_Modals/DeleteModal';
import ImagesSlideView from 'Components/ImagesSlideView';
import { useExteriorSignageCategories } from 'services/Data/ExteriorSignageCategoryService';
import { getLocalizedCategoryLabel, isLimitedExteriorSignageCategory } from 'services/Utilities/categoryHelpers';

const useStyles = makeStyles(theme => ({
  mainButton: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 0 1rem rgba(255, 222, 0, 0.9)',
    },
  },
  guideLink: {
    color: 'rgba(51, 51, 51, 0.8)',
    textDecoration: 'underline',
    '&:hover': {
      color: 'rgba(27, 27, 27, 0.8)',
      textDecoration: 'underline',
    },
  },
}));

const ExteriorSignageQuote = () => {
  const classes = useStyles();

  let hist = useHistory();

  const [itemToDelete, setItemToDelete] = useState();

  const { t } = useTranslation();
  const accountReducer = useSelector(state => state.AccountReducer);
  const isFrench = useMemo(() => t('locale') === 'fr', [t]);

  const { data: exteriorSignages, isLoading: gettingExteriorSignage } = useExteriorSignages();
  const { data: exteriorSignageCategories } = useExteriorSignageCategories();
  const { data: exteriorSignageQuote } = useActiveExteriorSignageQuote(accountReducer.isLoggedIn);
  const { getExteriorSignageImage } = useImage();
  const {
    mutateAsync: addExteriorSignageQuoteItem,
    isPending: addingExteriorSignageQuoteItem,
  } = usePostExteriorSignageQuoteItem(accountReducer.isDemo);
  const { mutate: deleteExteriorSignageQuoteItem } = useDeleteExteriorSignageQuoteItem();

  const [temporaryItem, setTemporaryItem] = useState(null);
  const [selectedImgId, setSelectedImgId] = useState(null);

  const selectedSignageType = useMemo(() => exteriorSignages?.find(es => es.id === temporaryItem?.signageType), [
    exteriorSignages,
    temporaryItem?.signageType,
  ]);

  const exteriorSignageCategoryById = useMemo(
    () => new Map((exteriorSignageCategories || []).map(category => [category.id, category])),
    [exteriorSignageCategories],
  );

  const getPrice = useCallback(
    signageType => {
      const signageToUse = signageType || selectedSignageType;
      return signageToUse?.oneBrandPrice || 0;
    },
    [selectedSignageType],
  );

  const alreadyInQuoteExteriorSignageIds = useMemo(
    () => exteriorSignageQuote?.quoteItems?.map(i => i.exteriorSignage_Id) || [],
    [exteriorSignageQuote?.quoteItems],
  );
  const sortedExteriorSignages = useMemo(
    () =>
      exteriorSignages?.filter(es => !alreadyInQuoteExteriorSignageIds.includes(es.id)).sort((a, b) => a.order - b.order) ||
      [],
    [alreadyInQuoteExteriorSignageIds, exteriorSignages],
  );

  const availableExteriorSignageCategories = useMemo(() => {
    const sortedCategoryIds = [
      ...new Set((sortedExteriorSignages || []).map(signage => signage.exteriorSignageCategory_Id).filter(Boolean)),
    ];

    return sortedCategoryIds.map(categoryId => exteriorSignageCategoryById.get(categoryId) || { id: categoryId });
  }, [exteriorSignageCategoryById, sortedExteriorSignages]);

  const selectedSignageCategory = useMemo(
    () =>
      availableExteriorSignageCategories.find(category => category.id === temporaryItem?.signageCategoryId) ||
      exteriorSignageCategoryById.get(temporaryItem?.signageCategoryId),
    [availableExteriorSignageCategories, exteriorSignageCategoryById, temporaryItem?.signageCategoryId],
  );

  const showQuantitySelector = useMemo(() => isLimitedExteriorSignageCategory(selectedSignageCategory), [
    selectedSignageCategory,
  ]);

  const handleAddSignage = useCallback(
    _ => {
      const exteriorSignageQuoteItem = {
        id: uuidv4(),
        exteriorSignage_Id: temporaryItem?.signageType,
        numberOfBrand: 1,
        unitPrice: getPrice(),
        currencyName: selectedSignageType?.currencyName,
        sku: selectedSignageType?.sku,
        quantity: temporaryItem?.quantity || 1,
        exteriorSignageQuote_Id: exteriorSignageQuote?.id,
      };
      addExteriorSignageQuoteItem(exteriorSignageQuoteItem).then(_ => {
        setTemporaryItem({});
      });
    },
    [
      addExteriorSignageQuoteItem,
      exteriorSignageQuote?.id,
      getPrice,
      selectedSignageType?.currencyName,
      selectedSignageType?.sku,
      temporaryItem?.quantity,
      temporaryItem?.signageType,
    ],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setItemToDelete(null);
  }, [setItemToDelete]);

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box className={classes.pageFull}>
        <DeleteModal
          handleCloseModal={handleCloseDeleteModal}
          item={itemToDelete}
          message={t('deleteItemFromExteriorSignageQuoteText', {
            sku: itemToDelete?.sku,
            name: itemToDelete?.name,
          })}
          deleteAction={deleteExteriorSignageQuoteItem}
        />

        <GridContainer justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
          <GridItem xs={12} xl={8} lg={7} md={6}>
            <ImagesSlideView
              isLoading={gettingExteriorSignage}
              images={selectedSignageType?.images}
              getImageUrl={imgId => getExteriorSignageImage(selectedSignageType?.id, imgId)}
              onUpdateImage={img => setSelectedImgId(img?.id)}
            />
            {/* <Box minWidth={'250px'} textAlign={'center'}>
              <img
                src={
                  selectedSignageType?.images?.length > 0
                    ? getExteriorSignageImage(selectedSignageType?.id, selectedSignageType?.images[0].id)
                    : '/images/brp-logo.png'
                }
                alt="Header"
                style={{ height: '300px', objectFit: 'contain', cursor: 'pointer' }}
              />
            </Box> */}
          </GridItem>
          <GridItem xs={12} xl={4} lg={5} md={6}>
            <CmtCard sx={{ height: '100%' }} className={classes.itemCard}>
              <CmtCardContent className={classes.itemCardContent} sx={{ height: '100%' }}>
                <Box display="flex" flexDirection={'column'} style={{ width: '100%', marginBottom: '1vh' }}>
                  <Typography variant="h1">{t('exteriorSignage')}</Typography>
                  <Typography variant="h5" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                    {t('quote')}
                  </Typography>
                  <Divider />
                </Box>

                <CustomAutocomplete
                  labelText={t('signageCategory')}
                  hasSkeleton
                  isLoading={false}
                  options={availableExteriorSignageCategories || []}
                  formControlProps={{
                    fullWidth: true,
                  }}
                  error={false}
                  autocompleteProps={{
                    onChange: (event, value) => {
                      if (value?.id !== temporaryItem?.signageCategoryId) {
                        setTemporaryItem({ signageCategoryId: value?.id, quantity: 1 });
                      }
                    },
                    isOptionEqualToValue: (exteriorSignageCategory, value) => exteriorSignageCategory.id === value?.id,
                    value: selectedSignageCategory || null,
                    getOptionLabel: exteriorSignageCategory => {
                      return getLocalizedCategoryLabel(exteriorSignageCategory, isFrench);
                    },
                    filterOptions: (exteriorSignageCategories, state) => {
                      var searchTerms = state.inputValue.toLowerCase().split(' ');
                      var filteredDiscountTypes = exteriorSignageCategories.filter(exteriorSignageCategory =>
                        searchTerms.every(searchTerm =>
                          getLocalizedCategoryLabel(exteriorSignageCategory, isFrench)
                            ?.toLowerCase()
                            .includes(searchTerm),
                        ),
                      );

                      return filteredDiscountTypes;
                    },
                    renderOption: (props, exteriorSignageCategory, { inputValue }) => {
                      const result = getHighlightParts(
                        getLocalizedCategoryLabel(exteriorSignageCategory, isFrench),
                        inputValue,
                      );

                      return (
                        <div className={classes.autoCompleteItem} {...props}>
                          {result}
                        </div>
                      );
                    },
                  }}
                />

                <CustomAutocomplete
                  labelText={t('signageType')}
                  disabled={!temporaryItem?.signageCategoryId}
                  hasSkeleton
                  isLoading={gettingExteriorSignage}
                  options={
                    sortedExteriorSignages?.filter(
                      es => es.exteriorSignageCategory_Id === temporaryItem?.signageCategoryId,
                    ) || []
                  }
                  formControlProps={{
                    fullWidth: true,
                  }}
                  error={false}
                  autocompleteProps={{
                    onChange: (event, value) => {
                      if (value?.id !== temporaryItem?.signageType) {
                        setTemporaryItem({
                          signageCategoryId: temporaryItem.signageCategoryId,
                          quantity: temporaryItem.quantity,
                          signageType: value?.id,
                        });
                      }
                    },
                    isOptionEqualToValue: (panelType, value) => panelType.id === value,
                    value: exteriorSignages?.find(esc => esc.id === temporaryItem?.signageType) || null,
                    getOptionLabel: exteriorSignage => {
                      return (
                        (exteriorSignage &&
                          t('formatSignageType', {
                            signageType: exteriorSignage.name,
                            price: getPrice(exteriorSignage),
                            formatParams: {
                              price: { currency: 'USD', locale: 'en-US' },
                            },
                          })) ||
                        ''
                      );
                    },
                    filterOptions: (exteriorSignages, state) => {
                      var searchTerms = state.inputValue.toLowerCase().split(' ');
                      var filteredDiscountTypes = exteriorSignages.filter(exteriorSignage =>
                        searchTerms.every(searchTerm =>
                          t('formatSignageType', {
                            signageType: exteriorSignage.name,
                            price: getPrice(exteriorSignage),
                            formatParams: {
                              price: { currency: 'USD', locale: 'en-US' },
                            },
                          })
                            ?.toLowerCase()
                            .includes(searchTerm),
                        ),
                      );

                      return filteredDiscountTypes;
                    },
                    renderOption: (props, exteriorSignage, { inputValue }) => {
                      const result = getHighlightParts(
                        t('formatSignageType', {
                          signageType: exteriorSignage.name,
                          price: getPrice(exteriorSignage),
                          formatParams: {
                            price: { currency: 'USD', locale: 'en-US' },
                          },
                        }),
                        inputValue,
                      );

                      return (
                        <div className={classes.autoCompleteItem} {...props}>
                          {result}
                        </div>
                      );
                    },
                  }}
                />

                <Box display="flex" flexDirection={'column'}>
                  {selectedSignageType && <Divider sx={{ my: 3 }} />}
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: '1vh',
                      flexDirection: 'column',
                    }}>
                    {selectedSignageType && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}>
                        <Box>
                          <Typography variant="h2">{t('price')}</Typography>
                        </Box>
                        <Typography fontSize={25} fontWeight="bold">
                          <Currency
                            quantity={getPrice() * (temporaryItem?.quantity || 1)}
                            currency={exteriorSignageQuote?.currencyName}
                          />
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {selectedSignageType && <Divider sx={{ my: 2 }} />}

                  {selectedSignageType && <Typography fontSize={25}></Typography>}

                  <Box
                    sx={{
                      justifyContent: selectedSignageType && showQuantitySelector ? 'space-between' : 'flex-end',
                      alignItems: 'center',
                      display: 'flex',
                      pt: 0,
                    }}>
                    {selectedSignageType && showQuantitySelector && (
                      <Box
                        fontSize={20}
                        style={{ width: '15vh' }}
                        sx={{ mt: 4, mb: 5 }}
                        display="flex"
                        justifyContent={'space-between'}
                        alignItems={'center'}>
                        <Fab
                          size="small"
                          color="secondary"
                          className={classes.squareFab}
                          sx={{
                            color: '#000',
                          }}
                          disabled={temporaryItem.quantity <= 0}
                          onClick={() => setTemporaryItem({ ...temporaryItem, quantity: temporaryItem.quantity - 1 })}>
                          <Remove />
                        </Fab>
                        <Typography>{temporaryItem.quantity}</Typography>
                        <Fab
                          size="small"
                          color="secondary"
                          className={classes.squareFab}
                          sx={{
                            color: '#000',
                          }}
                          disabled={temporaryItem.quantity >= 3}
                          onClick={() => setTemporaryItem({ ...temporaryItem, quantity: temporaryItem.quantity + 1 })}>
                          <Add />
                        </Fab>
                      </Box>
                    )}
                    <Button
                      key={uuidv4()}
                      sx={{ borderRadius: 0 }}
                      disabled={
                        addingExteriorSignageQuoteItem || !temporaryItem?.signageCategoryId || !temporaryItem?.signageType
                      }
                      variant={'contained'}
                      color={'secondary'}
                      onClick={handleAddSignage}>
                      <Typography
                        sx={{
                          color: 'black !important',
                          fontSize: 16,
                          fontWeight: 700,
                          paddingRight: 10,
                          paddingLeft: 10,
                          paddingTop: 2,
                          paddingBottom: 2,
                        }}>
                        {t('add')}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              </CmtCardContent>
            </CmtCard>

            <CmtCard sx={{ mt: 2, minHeight: '20vh' }}>
              <CmtCardContent>
                {selectedSignageType ? (
                  <Box>
                    <Typography variant="h2">{selectedSignageType.name}</Typography>
                    <Typography variant="h6">{selectedSignageType.sku}</Typography>
                    <Typography variant="h5" sx={{ mt: 2 }}>{`${
                      selectedSignageType.variableWidth
                        ? t('variantSignageWidth')
                        : selectedSignageType.width
                        ? t('signageWidth', { width: selectedSignageType.width })
                        : ''
                    }${
                      selectedSignageType.height ? `, ${t('signageHeight', { height: selectedSignageType.height })}` : ''
                    }`}</Typography>
                    {selectedSignageType.images?.find(i => i.id === selectedImgId)?.imgTotalSquareFeet && (
                      <Typography variant="h5" sx={{ mt: 2 }}>
                        {t('signageTotalSquareFeet', {
                          totalSquareFeet: selectedSignageType.images?.find(i => i.id === selectedImgId).imgTotalSquareFeet,
                        })}
                      </Typography>
                    )}
                    <Typography sx={{ mt: 5 }} variant="h5">
                      {selectedSignageType.description}
                    </Typography>

                    {selectedSignageType.warning && (
                      <Typography sx={{ mt: 2 }} variant="h6">
                        {selectedSignageType.warning}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  t('no_signage_category_selected')
                )}
              </CmtCardContent>
            </CmtCard>
          </GridItem>
          <GridItem xs={12} textAlign="right">
            <Button
              sx={{ borderRadius: 0 }}
              disabled={(exteriorSignageQuote?.quoteItems?.length || 0) <= 0}
              variant={'contained'}
              color={'secondary'}
              onClick={() => {
                hist.push('/dealer/home-page/exterior-signage/check-out');
              }}>
              <Typography
                sx={{
                  color: 'black !important',
                  fontSize: 16,
                  fontWeight: 700,
                  paddingRight: 10,
                  paddingLeft: 10,
                  paddingTop: 2,
                  paddingBottom: 2,
                }}>
                {t('proceedCheckoutExteriorSignageQuote')}
              </Typography>
            </Button>
          </GridItem>
        </GridContainer>
      </Box>
    </Slide>
  );
};

export default ExteriorSignageQuote;
